import { LINKS, CAMPAIGN_IMAGES } from "../../config/links";

const PACKAGES = [
  {
    id: "starter-setup",
    tag: "Entry",
    name: "Starter Automation Setup",
    headline:
      "For advisors who already have leads — but no system catching them.",
    outcome:
      "Stop losing opportunities due to delayed follow-up and manual systems.",
    cta: {
      label: "Build My System",
      href: LINKS.starterSetup,
      testid: "advisor-pkg-cta-starter",
    },
    featured: false,
  },
  {
    id: "basic-package",
    tag: "Most Chosen",
    name: "Basic Package",
    headline: "Consistent lead nurturing and appointment conversion.",
    outcome: "More booked appointments. Better follow-up. Less chasing.",
    cta: {
      label: "Scale My Pipeline",
      href: LINKS.basicSetup,
      testid: "advisor-pkg-cta-basic",
    },
    featured: true,
  },
  {
    id: "thanos-package",
    tag: "Full Automation",
    name: "The Thanos Package",
    headline: "Fully automated advisor growth ecosystem.",
    outcome:
      "Predictable inbound opportunities with systems running behind the scenes.",
    cta: {
      label: "Activate Full Automation",
      href: LINKS.thanosSetup,
      testid: "advisor-pkg-cta-thanos",
    },
    featured: false,
  },
];

export default function AdvisorPackages() {
  return (
    <section
      id="advisor-packages"
      data-testid="advisor-packages-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[480px] w-[860px] bg-amber-500/[0.06] blur-[120px] rounded-full" aria-hidden />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-center">
          <div className="lg:col-span-7">
            <div className="reveal flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-amber-500/70" />
              <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
                Advisor Automation Systems
              </span>
            </div>
            <h2
              data-testid="advisor-packages-headline"
              className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
              style={{ transitionDelay: "80ms" }}
            >
              Stop manually chasing growth.{" "}
              <span className="italic text-amber-400">Start building it.</span>
            </h2>
            <p
              className="reveal mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl"
              style={{ transitionDelay: "140ms" }}
            >
              Three ascension paths. One outcome — a follow-up engine that
              works while you live your life.
            </p>
          </div>

          {/* The Chase Is Over image — cinematic feature */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-3 bg-amber-500/15 blur-2xl opacity-70" aria-hidden />
              <div className="relative overflow-hidden border border-amber-500/30 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
                <img
                  src={CAMPAIGN_IMAGES.chaseIsOver}
                  alt="The chase is over — stop guessing, start closing"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1733]/60 via-transparent to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-[#0a1733] border border-amber-500/40 px-3 py-1.5">
                <span className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400">
                  The Chase Is Over
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Free strategy call banner */}
        <div
          data-testid="advisor-free-call-banner"
          className="reveal mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 bg-gradient-to-r from-emerald-500/[0.08] via-emerald-500/[0.03] to-transparent border border-emerald-500/25 px-6 sm:px-8 py-5"
        >
          <div className="flex items-center gap-4">
            <div className="font-display text-3xl text-emerald-400 leading-none">$0</div>
            <div>
              <div className="font-display text-lg text-white leading-tight">
                Not sure which path is right? Start with a free call.
              </div>
              <div className="text-xs text-slate-400 mt-1">
                30-minute audit · Live system mapping · No pressure.
              </div>
            </div>
          </div>
          <a
            href={LINKS.calendar}
            data-testid="advisor-free-call-cta"
            className="inline-flex items-center gap-2 border border-emerald-400/60 hover:bg-emerald-500/10 text-emerald-300 px-6 py-3 text-[12px] uppercase tracking-[0.2em] font-semibold transition-colors"
          >
            Book Strategy Session
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {PACKAGES.map((tier, i) => (
            <TierCard key={tier.id} tier={tier} index={i} />
          ))}
        </div>

        <p className="reveal mt-12 text-center text-xs uppercase tracking-[0.24em] text-slate-500">
          Custom workflows · Done-for-you systems · Built by an advisor
        </p>
      </div>
    </section>
  );
}

function TierCard({ tier, index }) {
  const featured = tier.featured;
  return (
    <div
      data-testid={`advisor-pkg-${tier.id}`}
      className={`reveal relative flex flex-col p-8 lg:p-10 transition-all duration-400 hover:-translate-y-1.5 ${
        featured
          ? "bg-gradient-to-b from-slate-900/80 to-slate-950 border border-amber-500/40 shadow-[0_0_60px_rgba(217,119,6,0.15)] shimmer-border"
          : "bg-[#0e1e44]/70 border border-white/10 hover:border-white/25"
      }`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {featured && (
        <div className="absolute -top-3 left-8 bg-amber-500 text-slate-950 text-[10px] font-bold tracking-[0.24em] uppercase px-3 py-1">
          Most Chosen
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <span className={`font-sans-body uppercase text-[10px] tracking-[0.3em] ${featured ? "text-amber-400" : "text-slate-400"}`}>
          {tier.tag}
        </span>
        <span className="font-display text-lg text-slate-500">0{index + 1}</span>
      </div>

      <h3 className="font-display text-3xl sm:text-[32px] text-white leading-tight">
        {tier.name}
      </h3>

      <p className="mt-5 text-slate-200 leading-relaxed italic font-display text-lg">
        "{tier.headline}"
      </p>

      <div className="mt-8 pt-6 border-t border-white/10 flex-1">
        <div className="font-sans-body uppercase text-[9px] tracking-[0.28em] text-emerald-400 mb-3">
          Outcome
        </div>
        <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
          {tier.outcome}
        </p>
      </div>

      <a
        href={tier.cta.href}
        data-testid={tier.cta.testid}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-8 inline-flex items-center justify-center gap-3 px-6 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
          featured
            ? "bg-amber-500 hover:bg-amber-400 text-slate-950"
            : "border border-white/20 hover:border-amber-400/70 text-white hover:text-amber-300"
        }`}
      >
        {tier.cta.label}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}
